import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

import { Text } from "@ps-design-system/components/Text";
import { useToast } from "@ps-design-system/components/Toast";

import { useIntl } from "react-intl-compat";

import {
  getFlatRatePrice,
  getFoodAndDrinkMinimum,
} from "../../../../components/src/app-mylistings/utils/listing-fsv.utils";
import {
  type FsvPricingErrorBody,
  useUpdateFsvListingMutation,
} from "../../../../components/src/services/listings.service";
import type { Listing } from "../../../../types/listings.types";

import { ROUNDED_UP_MESSAGES } from "./ListingFsvForm/ListingFsvForm.consts";
import {
  buildListingFsvFormSchema,
  ListingFsvFormValues,
} from "./ListingFsvForm/ListingFsvForm.schema";
import {
  buildFsvListingUpdatePayload,
  getFsvSaveError,
  getRoundedUpAmounts,
} from "./ListingFsvForm/ListingFsvForm.utils";
import {
  formatCurrencyAmount,
  getCurrencySymbol,
  getGratuityPercent,
} from "./ListingFsvPage.utils";
import {
  SAVE_ERROR_TYPE,
  useFsvPricingValidation,
} from "./useFsvPricingValidation";

const getErrorBody = (error: unknown) =>
  (error as { data?: FsvPricingErrorBody })?.data;

/**
 * Builds the form for the FSV page (F&B minimum, space rental, gratuity,
 * room type, menu), prefilled from the listing, and wires up its save action.
 */
const useListingFsvForm = (listing?: Listing) => {
  const intl = useIntl();
  const flatRatePrice = getFlatRatePrice(listing?.prices);
  const spaceRental = flatRatePrice?.value ?? 0;
  const gratuityRate = listing ? getGratuityPercent(listing) : 0;
  const currency =
    flatRatePrice?.currency ?? listing?.prices?.[0]?.currency ?? "USD";

  const form = useForm<ListingFsvFormValues>({
    resolver: zodResolver(buildListingFsvFormSchema({ intl })),
    // The page only renders this form for FSV listings, which always have
    // `full_service` (and its `room_type`)
    values: listing?.full_service
      ? {
          foodAndDrinkMinimum: getFoodAndDrinkMinimum(
            listing.inventory?.inventory,
          ),
          hasSpaceRental: spaceRental > 0,
          spaceRental,
          hasGratuity: gratuityRate > 0,
          gratuityRate,
          roomType: listing.full_service.room_type,
          menuUrl: listing.full_service.menu_url,
        }
      : undefined,
  });

  const { toast } = useToast();
  const [updateFsvListing] = useUpdateFsvListingMutation();
  const [errorMsg, setErrorMsg] = React.useState<string>();
  // From Save until the save fails; a successful save navigates away, so
  // the form stays locked until it does
  const [isSaving, setIsSaving] = React.useState(false);
  const {
    ensurePricingValidated,
    isEditingPricing,
    isValidatingPricing,
    startEditingPricing,
    validatePricing,
  } = useFsvPricingValidation({
    currency,
    form,
    listingId: listing?._id ?? "",
  });

  /** Rounds fractional amounts up to whole numbers, with a toast for each. */
  const roundUpAmounts = React.useCallback(() => {
    getRoundedUpAmounts(form.getValues()).forEach(({ field, amount }) => {
      form.setValue(field, amount, { shouldDirty: true, shouldValidate: true });
      toast({
        title: "",
        description: React.createElement(
          Text,
          { typeface: "body-1-m", element: "p", className: "tw-mb-0" },
          field === "gratuityRate"
            ? intl.formatMessage(ROUNDED_UP_MESSAGES.percent, { amount })
            : intl.formatMessage(ROUNDED_UP_MESSAGES.amount, {
                amount: formatCurrencyAmount(intl, amount, currency),
              }),
        ),
        variant: "black",
        duration: 3000,
      });
    });
  }, [currency, form, intl, toast]);

  const submit = React.useCallback(async (): Promise<boolean> => {
    let success = false;

    // A pasted fraction may not have been blurred yet
    roundUpAmounts();

    // Let any space rental bump land before the form values are read.
    // handleSubmit reruns the schema and would drop the check's errors, so
    // stop here while a pricing field still shows one
    const isPricingValid = await ensurePricingValidated();
    if (!isPricingValid) {
      return false;
    }

    await form.handleSubmit(async (values) => {
      if (!listing) {
        return;
      }

      try {
        await updateFsvListing({
          listingId: listing._id,
          payload: buildFsvListingUpdatePayload(
            values,
            form.formState.dirtyFields,
          ),
        }).unwrap();
        success = true;
      } catch (error) {
        const { amount, field, message } = getFsvSaveError(getErrorBody(error));
        const formattedMessage = intl.formatMessage(
          message,
          amount != null
            ? { amount: formatCurrencyAmount(intl, amount, currency) }
            : undefined,
        );

        // Space rental's input is hidden while its toggle is off; fall back
        // to the summary message then
        const isFieldVisible =
          field !== "spaceRental" || form.getValues("hasSpaceRental");

        if (field && isFieldVisible) {
          // Shown on the input itself (red border + message underneath)
          form.setError(
            field,
            { type: SAVE_ERROR_TYPE, message: formattedMessage },
            { shouldFocus: true },
          );
        } else {
          setErrorMsg(formattedMessage);
        }
      }
    })();

    return success;
  }, [
    currency,
    ensurePricingValidated,
    form,
    intl,
    listing,
    roundUpAmounts,
    updateFsvListing,
  ]);

  const onSave = React.useCallback(async (): Promise<boolean> => {
    setErrorMsg(undefined);
    setIsSaving(true);
    let success = false;

    try {
      success = await submit();
    } finally {
      if (!success) {
        setIsSaving(false);
      }
    }

    return success;
  }, [submit]);

  return {
    currencySymbol: getCurrencySymbol(currency),
    errorMsg,
    form,
    isEditingPricing,
    isSaving,
    isValidatingPricing,
    onSave,
    roundUpAmounts,
    startEditingPricing,
    validatePricing,
  };
};

export { useListingFsvForm };
